import * as React from 'react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { SearchFilters } from '@th/shared/src/types/SearchFilters';
import SearchFilterView from './searchFilters/searchFilterView/SearchFilterView';
import { useSearch } from '../contexts/SearchContext';
import pick from 'lodash.pick';
import ModalWithOverlay from './ModalWithOverlay';
import numberOfNonDefaultFilters from '@th/shared/src/helpers/searchFilters/numberOfNonDefaultFilters';
import Loader from './Loader';
import SvgIcon from './icons/SvgIcon';

export type NlpSearchStatusModalHandle = {
  openWithLoadingState: (nlpPhrase: string) => void;
  close: () => void;
};

type Props = {};

const FIRST_STAY_OPEN_MILLISECONDS = 4000;
const AFTER_EDIT_STAY_OPEN_MILLISECONDS = 2000;

const NlpSearchStatusModal = forwardRef<NlpSearchStatusModalHandle, Props>(
  ({}, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [currentSearch, setCurrentSearch] = useState<{
      nlpPhrase: string;
      searchFilters: SearchFilters;
    }>({
      nlpPhrase: '',
      searchFilters: {},
    });
    const { searchFilters, updateFilters } = useSearch();
    const timeoutRef = useRef<NodeJS.Timeout>(); // timer id

    function scheduleAutoClose(timeout) {
      timeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, timeout);
    }

    useImperativeHandle(ref, () => {
      return {
        openWithLoadingState: (nlpPhrase: string) => {
          setCurrentSearch({ nlpPhrase, searchFilters: {} });
          setIsLoading(true);
          setIsOpen(true);
        },
        close: () => {
          setIsLoading(false);
          setIsOpen(false);
        },
      };
    });

    if (!isOpen) return null;

    // show actual searchFilters, but only for the keys that are set in the current search
    const searchFiltersToShow = pick(
      searchFilters,
      Object.keys(currentSearch.searchFilters),
    );

    const numberOfFiltersSet = numberOfNonDefaultFilters(searchFilters);

    return (
      <ModalWithOverlay
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setIsLoading(false);
          setCurrentSearch({ nlpPhrase: '', searchFilters: {} });
        }}
        className="p-8 w-full md:min-w-[520px]"
      >
        {isLoading ? (
          <div className="flex flex-1 flex-col items-start">
            <p className="font-bold mb-8">{currentSearch.nlpPhrase}</p>
            <div className="flex flex-row items-center ">
              <Loader size={'large'} />
              <p className="ml-4">Searching...</p>
            </div>
          </div>
        ) : (
          <div
            className="flex flex-1 flex-col"
            onMouseOver={() => {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }
            }}
            onMouseEnter={() => {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }
            }}
            onMouseLeave={() => {
              scheduleAutoClose(AFTER_EDIT_STAY_OPEN_MILLISECONDS);
            }}
          >
            <div className="flex justify-between">
              <p className="font-bold">{currentSearch.nlpPhrase}</p>
              <button
                onClick={() => {
                  setIsOpen(false);
                }}
                className="pb-2"
              >
                <SvgIcon name={'HiX'} />
              </button>
            </div>
            <p>
              {numberOfFiltersSet} filter{numberOfFiltersSet === 1 ? '' : 's'}{' '}
              applied
            </p>
            <SearchFilterView
              searchFilters={searchFiltersToShow}
              onChange={updateFilters}
              enableListAndSoldDateFilters={true}
              viewLevel={'none'}
              showFiltersWhenSupplied={true}
              isAdmin={false}
            />
          </div>
        )}
      </ModalWithOverlay>
    );
  },
);

NlpSearchStatusModal.displayName = 'NlpSearchStatusModal';
export default NlpSearchStatusModal;
